import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  ArtefactTemplate,
  CreateArtefactTemplateInput,
  UpdateArtefactTemplateInput,
} from '../../types/artefact-templates'
import type { TemplateUserRole } from '../../utils/template-permissions'
import { VelociraptorConfigModal } from '../velociraptor/VelociraptorConfigModal'
import {
  ManagerHeader,
  ManagerLoading,
  TemplatesSection,
} from './artefact-template-manager/artefact-template-manager.parts'
import { TemplateCard } from './artefact-template-manager/template-card'
import { useArtefactTemplateManager } from './artefact-template-manager/use-artefact-template-manager'

interface ArtefactTemplateManagerProps {
  templates: ArtefactTemplate[]
  loading?: boolean
  scope: 'organisation' | 'case'
  caseId?: string | undefined
  userRole: TemplateUserRole
  /** @deprecated — modal handles saves internally; kept for call-site compat */
  onCreate?: (input: CreateArtefactTemplateInput) => Promise<ArtefactTemplate | null>
  /** @deprecated — modal handles saves internally; kept for call-site compat */
  onUpdate?: (
    templateId: string,
    input: UpdateArtefactTemplateInput
  ) => Promise<ArtefactTemplate | null>
  onDelete: (templateId: string) => Promise<boolean>
  onRefetch: () => void
}

export function ArtefactTemplateManager(props: ArtefactTemplateManagerProps): ReactElement {
  const { templates, loading, scope, caseId, userRole, onDelete, onRefetch } = props
  const { t } = useTranslation('cases')
  const state = useArtefactTemplateManager({ onDelete, onRefetch })

  const scopeTemplates = templates.filter((tmpl) => tmpl.scope === scope)
  const systemTemplates = templates.filter((tmpl) => tmpl.scope === 'system')

  if (loading) return <ManagerLoading />

  const scopeHeading =
    scope === 'organisation'
      ? t('collections.artefactTemplates.orgTemplates')
      : t('collections.artefactTemplates.caseTemplates')

  return (
    <div className="space-y-6">
      <ManagerHeader onCreate={state.openCreateModal} t={t} />
      <TemplatesSection
        heading={scopeHeading}
        templates={scopeTemplates}
        emptyMessage={t('collections.artefactTemplates.noTemplates')}
        renderItem={(template) => (
          <TemplateCard
            key={template.id}
            template={template}
            deleteConfirm={state.deleteConfirm === template.id}
            onEdit={() => {
              state.openEditModal(template)
            }}
            onDelete={() => {
              state.requestDelete(template.id)
            }}
            onDeleteConfirm={() => {
              void state.confirmDelete(template.id)
            }}
            onDeleteCancel={state.cancelDelete}
          />
        )}
      />
      {systemTemplates.length > 0 && (
        <TemplatesSection
          heading={t('collections.artefactTemplates.systemTemplates')}
          templates={systemTemplates}
          renderItem={(template) => <TemplateCard key={template.id} template={template} isSystem />}
        />
      )}
      <VelociraptorConfigModal
        isOpen={state.modalOpen}
        onClose={state.closeModal}
        onTemplateSaved={state.handleSaved}
        editTemplate={state.editingTemplate}
        context={{ scope, caseId, userRole }}
      />
    </div>
  )
}
